import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { Box, Grid, Typography, Divider, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { FormContext } from "context/formContext";
import {
  AddressValue, NameValue,
  StyledAddressWrapper,
  StyledBackButton,
  StyledBusinessTitle,
  StyledFooterBox,
  StyledNextButton,
} from "./styles";
import {
  useGetDoordashStoresListMutation,
  useGetGloriaFoodStoresListMutation,
  // useGetMealMeStoresListMutation,
} from "api";
import { setError } from "features/error";
import { useDispatch } from "react-redux";
import Preloader from "./Preloader/Preloader";
import TermsAndUsePrivacyPolicy from "./TermsPrivacy";
import sendToGTM from "./sendToGTM";

const normalizeUrlToOrigin = (url = "") => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return "";
  }
  // Allow users to enter the site without a protocol (e.g. "example.com").
  // Default to https:// when no protocol is provided.
  const urlWithProtocol = /^https?:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : `https://${trimmedUrl}`;
  try {
    return new URL(urlWithProtocol).origin;
  } catch (error) {
    return trimmedUrl;
  }
};

const getGloriaFoodUrl = (data) =>
  normalizeUrlToOrigin(
    data?.gloriaFoodUrl ||
      data?.addressInfo?.geoLocation?.debug?.website ||
      data?.addressInfo?.debug?.website ||
      ""
  );

const BusinessInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [getStoresList, { isLoading }] = useGetDoordashStoresListMutation();
  const [getGloriaFoodStoresList, { isLoading: isGloriaFoodLoading }] =
    useGetGloriaFoodStoresListMutation();
  const restaurantName =  localStorage.getItem('restaurantName');
  const { formData, updateFormData } = useContext(FormContext);
  const [restaurantList, setRestaurantList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [gloriaFoodUrl, setGloriaFoodUrl] = useState(getGloriaFoodUrl(formData));
  const isFirstStep = location.pathname === "/";
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goBack = () => {
    const event = "oco.multiStepFormStepChange";
    const value = 102;
    const label = "Step 2 to Step 1 (Business Info)";
    const gtmData = { value, label };
    sendToGTM(event, gtmData);
    navigate(-1);
  };

  const geNext = async () => {
    const trimmedGloriaFoodUrl = normalizeUrlToOrigin(gloriaFoodUrl.trim());
    if (trimmedGloriaFoodUrl) {
      const event = "oco.multiStepFormStepChange";
      const value = 2;
      const label = "Step 2 to Step 3 (Business Info)";
      const nextData = { gloriaFoodUrl: trimmedGloriaFoodUrl };
      try {
        const dataModel = {
          restaurantName: formData.restaurantName,
          addressInfo: formData.addressInfo,
          gloriaFoodUrl: trimmedGloriaFoodUrl,
        };
        const res = await getGloriaFoodStoresList(dataModel).unwrap();
        if (!res.error && res[0]) {
          Object.assign(nextData, res[0]);
        }
      } catch (error) {
        dispatch(
          setError({
            title: "",
            errorMessage: true,
            isServer: true,
            isTimeout: false,
          })
        );
        return;
      }
      const gtmData = { value, label, data: { ...formData, ...nextData } };
      updateFormData(nextData);
      sendToGTM(event, gtmData);
      navigate("/emailInfo");
      setErrorMessage("");
      return;
    }

    if (formData._id === null) {
      dispatch(
        setError({
          title: "",
          errorMessage: true,
          isServer: false,
          isTimeout: false,
        })
      );
    } else {
      if (!formData._id) {
        setErrorMessage("Please enter your website URL");
      } else {
        const event = "oco.multiStepFormStepChange";
        const value = 2;
        const label = "Step 2 to Step 3 (Business Info)";
        const gtmData = { value, label, data: formData };
        sendToGTM(event, gtmData);

        navigate("/emailInfo");
        setErrorMessage("");
      }
    }
  };

  useEffect(() => {
    const resolvedGloriaFoodUrl = getGloriaFoodUrl(formData);
    if (resolvedGloriaFoodUrl) {
      // A GloriaFood URL was already provided (e.g. user navigated back from a
      // later step). Restore the URL flow and keep the business-selection list
      // hidden, mirroring handleGloriaFoodUrlChange. Otherwise navigating back
      // would land the user on the business list instead of their filled URL.
      setGloriaFoodUrl(resolvedGloriaFoodUrl);
      setRestaurantList([]);
      return;
    }

    if (formData.restaurantName) {
      try {
        const dataModel = {
          restaurantName: formData.restaurantName,
          addressInfo: formData.addressInfo,
        };
        getStoresList(dataModel)
          .unwrap()
          .then((res) => {
            if (!res.error) {
              setRestaurantList(res);
              updateFormData(res[0]);
            }
          })
          .catch((error) => {
            dispatch(
              setError({
                title: "",
                errorMessage: true,
                isServer: true,
                isTimeout: false,
              })
            );
          });
      } catch (error) {
        console.error(error);

        // Expected output: ReferenceError: nonExistentFunction is not defined
        // (Note: the exact output may be browser-dependent)
      }
    }
  }, []);

  const handleSelectBusiness = (restaurant) => {
    const event = "oco.businessSelected";
    const value = 300;
    const label = "Business Selected";
    const gtmData = { value, label, data: restaurant };
    sendToGTM(event, gtmData);
    updateFormData(restaurant);
  };

  const handleGloriaFoodUrlChange = (event) => {
    const value = event.target.value;
    const normalizedValue = normalizeUrlToOrigin(value.trim());
    setGloriaFoodUrl(value);
    updateFormData({ gloriaFoodUrl: normalizedValue });
    if (normalizedValue) {
      setRestaurantList([]);
      setErrorMessage("");
    }
  };

  // const styleCheck = isMatchMobile
  //   ? {
  //     height: "100vh",
  //     overflow: "auto",
  //     mt: 3,
  //     display: "flex",
  //     flexDirection: "column",
  //     px: 0.5,
  //   }
  //   : { mt: 3, px: 0.5 };

  return (
    <Box>
      <StyledBusinessTitle
        sx={{
          fontSize: restaurantList.length ? "36px" : "22px",
          fontWeight: restaurantList.length ? 500 : 600,
          lineHeight: restaurantList.length ? "52.5px" : "1.2",
          textAlign: "left",
          mb: restaurantList.length ? 0 : 1.5,
        }}
      >
        {restaurantList.length ? "Select Your Business" : "GloriaFood Migration"}
      </StyledBusinessTitle>
      <Box
      // sx={{
      //   display: "flex",
      //   justifyContent: "space-between",
      //   flexDirection: "column",
      // }}
      >
        {(isLoading || isGloriaFoodLoading) && <Preloader overlay />}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: restaurantList.length ? 3 : 1 }}>
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              lineHeight: "21px",
              color: "#5F6470",
              textAlign: "left",
              maxWidth: "330px",
            }}
          >
            Paste your GloriaFood URL to import your restaurant details.
          </Typography>
          <TextField
            placeholder="GloriaFood URL"
            value={gloriaFoodUrl}
            onChange={handleGloriaFoodUrlChange}
            fullWidth
            size="small"
            variant="outlined"
            sx={{
              maxWidth: "330px",
              fontFamily: "Poppins, sans-serif",
              "& .MuiOutlinedInput-root": {
                borderRadius: "0px",
                backgroundColor: "#FFFFFF",
              },
              "& .MuiOutlinedInput-input": {
                fontSize: "14px",
                padding: "10px 12px",
              },
            }}
          />
          {restaurantList.map((item) => {
            return (
              <StyledAddressWrapper
                key={item._id}
                onClick={() => handleSelectBusiness(item)}
                isSelected={Boolean(item._id === formData._id)}
                sx={{ boxShadow: 3 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: "100px",
                      textAlign: "left",
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                    }}
                  >
                    <b>Name:</b>
                  </Typography>
                  <NameValue
                    sx={{
                     fontFamily: 'Poppins',
                     fontSize: '14px',
                     fontWeight: 400,
                    }}
                    align="left"
                  >
                    {restaurantName} <br />
                    <small>{`(${item.restaurantName})`}</small>
                  </NameValue>
                </Box>
                <Divider
                  sx={{
                    background: Boolean(item._id === formData._id) ?
                  `white` : '#C4C4C4',
                    height: '0.5px',
                    width: '100%',
                }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-start",
                    width: "100%",
                    padding: 2,
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: "100px",
                      textAlign: "left",
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 700,
                    }}
                  >
                    <b>Address:</b>
                  </Typography>
                  <AddressValue align="left">
                    {item?.addressInfo?.address1}
                    <br />
                    {`${item?.addressInfo?.city || ""} ${
                      item?.addressInfo?.state || ""
                    } ${item?.addressInfo?.zipCode || ""}`}
                  </AddressValue>
                </Box>
              </StyledAddressWrapper>
            );
          })}
          {restaurantList.length ? (
            <StyledAddressWrapper
              key={uuidv4()}
              onClick={() => handleSelectBusiness({ _id: null })}
              isSelected={Boolean(formData?._id === null)}
              sx={{ boxShadow: 3 }}
            >
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "center",
                  padding: '20px 0',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                None of Above
              </Typography>
            </StyledAddressWrapper>
          ) : null}
        </Box>
        <Box
          sx={{
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          {errorMessage && (
            <Typography color="error">{errorMessage}</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <StyledFooterBox sx={{ boxShadow: 4 }}>
          <Grid container sx={{ m: 0, p: 0 }}>
            <Grid item lg={6} md={12} xs={12} sm={12} sx={{ m: 0, p: 0 }}></Grid>
            <Grid item lg={6} md={12} xs={12} sm={12} sx={{ m: 0, p: 0, mt: 2 }}>
              {!isFirstStep && (
                <StyledBackButton variant="outlined" onClick={goBack}>
                  Back
                </StyledBackButton>
              )}
              <StyledNextButton variant="contained" onClick={geNext}>
                Next
              </StyledNextButton>
              <TermsAndUsePrivacyPolicy />
            </Grid>
          </Grid>
        </StyledFooterBox>
      </Box>
    </Box>
  );
};

export default BusinessInfo;
